import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Crown, X, ExternalLink, ArrowRight } from "lucide-react";
import { useActingAs } from "../context/ActingAsContext";

const NAVY = "#0A2540";
const GREEN = "#047857";
const GOLD = "#D4AF37";

/**
 * ActingAsBanner — sticky top bar shown whenever a recruiter has selected
 * a client to "act on behalf of". Sets the X-Resmo-Acting-As header on every
 * subsequent API call (via lib/api.js interceptor) so the toolkit runs
 * against the client's data.
 *
 * Rendered on every authenticated page (mounted in App.js).
 */
export default function ActingAsBanner() {
  const { actingAs, clearActingAs } = useActingAs();
  const navigate = useNavigate();
  if (!actingAs) return null;

  const exitActing = () => {
    clearActingAs();
    // Soft reload current route to make sure any cached data refetches under
    // the recruiter's own identity.
    window.location.reload();
  };

  return (
    <div
      className="sticky top-0 z-40 w-full"
      style={{
        background: `linear-gradient(90deg, ${NAVY} 0%, ${GREEN} 100%)`,
        borderBottom: `2px solid ${GOLD}`,
      }}
      data-testid="acting-as-banner"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-2 flex items-center gap-3 flex-wrap">
        <Crown size={14} style={{ color: GOLD, flexShrink: 0 }} />
        <span className="font-mono-rm uppercase text-[10px] tracking-widest"
              style={{ color: GOLD, fontWeight: 700 }}>
          · Acting on behalf of
        </span>
        <span className="font-display uppercase text-[13px] leading-none"
              style={{ color: "white", letterSpacing: "-0.01em" }}
              data-testid="acting-as-banner-name">
          {actingAs.client_name || actingAs.client_email}
        </span>
        {actingAs.client_email && actingAs.client_name && (
          <span className="font-mono-rm text-[10px] uppercase tracking-widest"
                style={{ color: "#A7F3D0" }}>
            · {actingAs.client_email}
          </span>
        )}
        <span className="flex-1" />
        <Link
          to="/recruiter"
          className="font-mono-rm text-[10px] uppercase tracking-widest inline-flex items-center gap-1 px-2 py-1 rounded"
          style={{ background: "rgba(255,255,255,0.1)", color: "white", border: "1px solid rgba(255,255,255,0.3)", fontWeight: 700 }}
          data-testid="acting-as-banner-switch"
        >
          Switch <ArrowRight size={10} />
        </Link>
        <button
          type="button"
          onClick={exitActing}
          className="font-mono-rm text-[10px] uppercase tracking-widest inline-flex items-center gap-1 px-2 py-1 rounded"
          style={{ background: GOLD, color: NAVY, border: `1px solid ${GOLD}`, fontWeight: 700 }}
          data-testid="acting-as-banner-exit"
        >
          Exit <X size={10} />
        </button>
      </div>
    </div>
  );
}
